import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import {
  Approver,
  DelegationUser,
  EscalationStep,
  NodeType,
  TriggerCondition,
  WorkflowConfig,
  WorkflowNode,
  WorkflowStatus,
} from '../../shared-services/pipeline-workflow.model';

const PENDING_WORKFLOW_DRAFT_KEY = 'platform_pending_workflow_draft';

interface ContentCardAction {
  label?: string;
  navigateTo?: string;
  externalUrl?: string;
  target?: string;
  openCreateModal?: boolean;
}

interface ContentCardItem {
  title?: string;
  description?: string;
  eyebrow?: string;
  moduleId?: string;
  icon?: string;
  status?: string;
  statusCountSource?: {
    url?: string;
    path?: string;
    singularLabel?: string;
    pluralLabel?: string;
  };
  tag?: string;
  badges?: Array<{ label?: string; tone?: string }>;
  meta?: Array<{ icon?: string; label?: string }>;
  version?: string;
  createCard?: boolean;
  topGradient?: string;
  topHeight?: string | number;
  disabled?: boolean;
  action?: ContentCardAction;
  detailRows?: Array<{ label: string; value: string }>;
  workflowConfig?: WorkflowConfig;
  workflowId?: string;
}

@Component({
  selector: 'app-content-card-grid',
  standalone: true,
  imports: [CommonModule, MatIconModule, FormsModule],
  templateUrl: './content-card-grid.component.html',
  styleUrl: './content-card-grid.component.scss',
})
export class ContentCardGridComponent implements OnInit, OnChanges {
  @Input() data: any;
  showCreateModal = false;
  newWorkflow = this.createEmptyWorkflowForm();
  apiCards: ContentCardItem[] | null = null;
  resolvedCardStatuses: Record<string, string> = {};
  private lastWorkflowApiSignature = '';
  private lastStatusCountSignature = '';

  constructor(
    private router: Router,
    private http: HttpClient,
  ) {}

  ngOnInit(): void {
    this.loadWorkflowCardsFromApi();
    this.loadCardStatusCounts();
  }

  ngOnChanges(_: SimpleChanges): void {
    this.loadWorkflowCardsFromApi();
    this.loadCardStatusCounts();
  }

  get sectionTitle(): string {
    return String(this.data?.sectionTitle ?? this.data?.title ?? '').trim();
  }

  get sectionSubtitle(): string {
    return String(this.data?.sectionSubtitle ?? this.data?.subtitle ?? '').trim();
  }

  get searchPlaceholder(): string {
    return String(this.data?.searchPlaceholder ?? '').trim();
  }

  get cards(): ContentCardItem[] {
    const configuredCards = Array.isArray(this.data?.cards) ? this.data.cards : [];
    if (!this.apiCards) {
      return configuredCards;
    }

    const createCards = configuredCards.filter((card: ContentCardItem) => card.createCard);
    return [...this.apiCards, ...createCards];
  }

  get columns(): string {
    const configured = Number(this.data?.columns);
    const count = Number.isFinite(configured) && configured > 0 ? configured : 2;
    return `repeat(${Math.min(count, 4)}, minmax(280px, 1fr))`;
  }

  get gap(): string {
    return this.normalizeCssSize(this.data?.gap, '24px');
  }

  get maxWidth(): string {
    return this.normalizeCssSize(this.data?.maxWidth, '1080px');
  }

  get cardMinHeight(): string {
    return this.normalizeCssSize(this.data?.cardMinHeight, '320px');
  }

  get gridMaxWidth(): string {
    return this.normalizeCssSize(this.data?.gridMaxWidth, this.maxWidth);
  }

  get variant(): string {
    return String(this.data?.variant || '').trim();
  }

  get createWorkflowModal(): any {
    return this.data?.createWorkflowModal ?? {};
  }

  get topHeight(): string {
    return this.normalizeCssSize(this.data?.topHeight, '160px');
  }

  getCardStyle(card: ContentCardItem): Record<string, string> {
    return {
      '--content-card-top-gradient': String(
        card.topGradient ||
        this.data?.topGradient ||
        'linear-gradient(135deg, rgba(205, 218, 245, 0.95), rgba(227, 248, 255, 0.92) 58%, rgba(210, 220, 246, 0.96))',
      ),
      '--content-card-top-height': this.normalizeCssSize(card.topHeight, this.topHeight),
      'min-height': this.cardMinHeight,
    };
  }

  onCardAction(card: ContentCardItem): void {
    if (card.disabled) return;

    if (card.createCard && card.action?.openCreateModal) {
      this.newWorkflow = this.createEmptyWorkflowForm();
      this.showCreateModal = true;
      return;
    }

    const route = String(card.action?.navigateTo || '').trim();
    if (route) {
      if (card.workflowId && this.hasWorkflowDetailApi()) {
        this.openWorkflowFromDetailApi(card, route);
        return;
      }

      if (card.workflowConfig) {
        this.storePendingWorkflow(card.workflowConfig);
      }

      this.router.navigateByUrl(route);
      return;
    }

    const externalUrl = String(card.action?.externalUrl || '').trim();
    if (externalUrl) {
      window.open(externalUrl, card.action?.target || '_self');
    }
  }

  onCardKeydown(event: KeyboardEvent, card: ContentCardItem): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.onCardAction(card);
  }

  hasAction(card: ContentCardItem): boolean {
    return !!String(card.action?.label || '').trim();
  }

  cardStatus(card: ContentCardItem): string {
    return this.resolvedCardStatuses[this.cardKey(card)] ?? String(card.status || '');
  }

  actionLabel(card: ContentCardItem): string {
    return String(card.action?.label || 'Open').replace(/->/g, '→').trim();
  }

  createWorkflow(): void {
    const name = this.newWorkflow.name.trim();
    if (!name) return;

    const draft = {
      id: `wf-${Date.now()}`,
      name,
      version: 'v1.0',
      status: 'DRAFT',
      lastEditedAgo: 'just now',
      nodes: [],
      description: this.newWorkflow.description.trim(),
      category: this.newWorkflow.category,
    };

    try {
      sessionStorage.setItem(PENDING_WORKFLOW_DRAFT_KEY, JSON.stringify(draft));
    } catch { /* storage unavailable */ }

    this.showCreateModal = false;
    const route = String(this.createWorkflowModal?.submitRoute || '/approval-workflows').trim();
    this.router.navigateByUrl(route || '/approval-workflows');
  }

  badgeToneClass(tone: string | undefined): string {
    const normalized = String(tone || 'neutral').trim().toLowerCase();
    return ['blue', 'green', 'neutral'].includes(normalized) ? `tone-${normalized}` : 'tone-neutral';
  }

  private loadWorkflowCardsFromApi(): void {
    const api = this.data?.workflowApi;
    const url = String(api?.url || '').trim();
    if (!url) return;

    const body = api?.body ?? {
      entityName: 'workflow_store',
      attributesList: ['workflows'],
      filterList: [
        {
          operator: '=',
          attributeName: 'is_enabled',
          attributeValue: true,
          logicalOperator: '',
        },
      ],
    };
    const signature = `${url}|${JSON.stringify(body)}`;
    if (signature === this.lastWorkflowApiSignature) return;
    this.lastWorkflowApiSignature = signature;

    this.http.post<any>(url, body).subscribe({
      next: (response) => {
        const workflows = this.extractWorkflows(response);
        this.apiCards = workflows.map((workflow) => this.mapWorkflowCard(workflow));
      },
      error: () => {
        this.apiCards = null;
      },
    });
  }

  private loadCardStatusCounts(): void {
    const configuredCards: ContentCardItem[] = Array.isArray(this.data?.cards) ? this.data.cards : [];
    const sourcedCards = configuredCards.filter((card) => String(card.statusCountSource?.url || '').trim());
    const signature = JSON.stringify(sourcedCards.map((card) => ({
      key: this.cardKey(card),
      source: card.statusCountSource,
    })));
    if (signature === this.lastStatusCountSignature) return;
    this.lastStatusCountSignature = signature;
    this.resolvedCardStatuses = {};

    sourcedCards.forEach((card) => {
      const source = card.statusCountSource!;
      this.http.get<any>(String(source.url).trim()).subscribe({
        next: (response) => {
          const collection = this.valueAtPath(response, source.path || '');
          if (!Array.isArray(collection)) return;

          // Exclude entities that are only shown as tab views nested under another
          // entity (e.g. DOA's range/approver tabs) so the count matches the number
          // of navigable cards actually rendered on the page.
          const nestedTabIds = new Set<string>(
            collection.flatMap((item: any) =>
              Array.isArray(item?.tabs) ? item.tabs.map((tab: any) => String(tab?.id)) : [],
            ),
          );
          const count = collection.filter((item: any) => !nestedTabIds.has(String(item?.id))).length;
          const label = count === 1
            ? String(source.singularLabel || source.pluralLabel || 'card')
            : String(source.pluralLabel || source.singularLabel || 'cards');
          this.resolvedCardStatuses = {
            ...this.resolvedCardStatuses,
            [this.cardKey(card)]: `${count} ${label}`,
          };
        },
      });
    });
  }

  private cardKey(card: ContentCardItem): string {
    return String(card.moduleId || card.title || '');
  }

  private valueAtPath(source: any, path: string): any {
    const normalizedPath = String(path || '').trim();
    if (!normalizedPath) return source;
    return normalizedPath.split('.').reduce((value, key) => value?.[key], source);
  }

  private extractWorkflows(response: any): any[] {
    const source = Array.isArray(response)
      ? response
      : Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.result)
          ? response.result
          : Array.isArray(response?.records)
            ? response.records
            : [];

    return source
      .map((item: any) => item?.workflows ?? item)
      .filter((workflow: any) => workflow && typeof workflow === 'object');
  }

  private mapWorkflowCard(workflow: any): ContentCardItem {
    const config = this.mapWorkflowConfig(workflow);
    const behavior = workflow?.behavior ?? {};
    const updated = this.formatDate(workflow?.updated_date);
    return {
      title: String(workflow?.name || workflow?.id || 'Untitled workflow'),
      description: String(workflow?.description || ''),
      badges: [
        { label: String(workflow?.status || 'DRAFT'), tone: this.statusTone(workflow?.status) },
        { label: String(workflow?.targetEntity || 'Workflow'), tone: 'blue' },
      ],
      meta: [
        { icon: 'tag', label: String(workflow?.id || 'No ID') },
        { icon: 'account_tree', label: `${config.nodes.length} nodes` },
        ...(updated ? [{ icon: 'calendar_today', label: updated }] : []),
      ],
      detailRows: [
        { label: 'Status', value: String(workflow?.status || 'DRAFT') },
        { label: 'Version', value: String(workflow?.version ?? '1') },
        { label: 'Next WF', value: String(workflow?.nextWFId || '-') },
        { label: 'On Reject', value: String(behavior?.onReject || '-') },
        { label: 'Timeout', value: String(behavior?.onTimeoutDefault || '-') },
        { label: 'Requester Cancel', value: behavior?.allowRequesterToCancel ? 'Yes' : 'No' },
        { label: 'Reject Comment', value: behavior?.commentRequiredOnReject ? 'Required' : 'Optional' },
      ],
      version: `v${workflow?.version ?? 1}`,
      workflowId: String(workflow?.id || ''),
      action: {
        label: 'Open',
        navigateTo: '/approval-workflows',
      },
      workflowConfig: config,
    };
  }

  private hasWorkflowDetailApi(): boolean {
    return !!this.workflowDetailUrl('');
  }

  private openWorkflowFromDetailApi(card: ContentCardItem, route: string): void {
    const url = this.workflowDetailUrl(card.workflowId || '');
    if (!url) {
      if (card.workflowConfig) {
        this.storePendingWorkflow(card.workflowConfig);
      }
      this.router.navigateByUrl(route);
      return;
    }

    const body = this.data?.workflowDetailApi?.body ?? {};
    this.http.post<any>(url, body, { headers: { 'Content-Type': 'application/json' } }).subscribe({
      next: (response) => {
        const workflow = this.extractWorkflowDetail(response);
        this.storePendingWorkflow(this.mapWorkflowConfig(workflow));
        this.router.navigateByUrl(route);
      },
      error: () => {
        if (card.workflowConfig) {
          this.storePendingWorkflow(card.workflowConfig);
        }
        this.router.navigateByUrl(route);
      },
    });
  }

  private workflowDetailUrl(workflowId: string): string {
    const detailApi = this.data?.workflowDetailApi ?? {};
    const template = String(detailApi.urlTemplate || '').trim();
    if (template) {
      return template.replace('{id}', encodeURIComponent(workflowId));
    }

    const baseUrl = String(detailApi.baseUrl || '').trim();
    if (!baseUrl) return '';
    if (!workflowId) return baseUrl;
    return `${baseUrl.replace(/\/$/, '')}/${encodeURIComponent(workflowId)}`;
  }

  private extractWorkflowDetail(response: any): any {
    return response?.workflows ?? response?.data?.workflows ?? response?.result?.workflows ?? response?.data ?? response?.result ?? response;
  }

  private mapWorkflowConfig(workflow: any): WorkflowConfig {
    const status = String(workflow?.status || 'DRAFT').toUpperCase() === 'PUBLISHED' ? 'PUBLISHED' : 'DRAFT';
    return {
      id: String(workflow?.id || `wf-${Date.now()}`),
      name: String(workflow?.name || workflow?.id || 'Untitled workflow'),
      version: `v${workflow?.version ?? 1}`,
      status: status as WorkflowStatus,
      lastEditedAgo: this.formatDate(workflow?.updated_date) || 'just now',
      nodes: Array.isArray(workflow?.approvalFlow)
        ? workflow.approvalFlow.map((flowNode: any, index: number) => this.mapWorkflowNode(flowNode, index, workflow))
        : [],
      apiWorkflow: workflow,
    };
  }

  private mapWorkflowNode(flowNode: any, index: number, workflow: any): WorkflowNode {
    const approvalType = Array.isArray(flowNode?.approvalType) ? flowNode.approvalType[0] : flowNode?.approvalType;
    const type = this.normalizeNodeType(approvalType?.type);
    const specs = Array.isArray(approvalType?.spec) ? approvalType.spec : [approvalType?.spec ?? {}];
    const primarySpec = specs[0] ?? {};
    const id = String(flowNode?.id || `${workflow?.id || 'workflow'}-node-${index + 1}`);
    const approvers = specs.flatMap((spec: any) => this.mapPeople(spec?.approvers)) as Approver[];
    const delegates = specs.flatMap((spec: any) => this.mapPeople(spec?.delegateApprover)) as DelegationUser[];
    const escalationChain = specs.flatMap((spec: any) => this.mapEscalations(spec?.sla?.escalationManagers));

    return {
      id,
      type,
      label: this.nodeLabel(type, index, primarySpec),
      isValid: true,
      triggerConditions: this.mapTriggerConditions(flowNode?.triggerConditions),
      approvers,
      decisionRule: {
        type: this.normalizeDecisionRule(primarySpec?.rule),
        minApprovals: this.toNumber(primarySpec?.minApprovals),
        totalApprovers: approvers.length || undefined,
        description: this.decisionDescription(type, primarySpec),
      },
      delegationEnabled: Boolean(primarySpec?.initDelegation),
      delegationUsers: delegates,
      sla: {
        reminderHours: this.toNumber(primarySpec?.sla?.reminderHours) ?? 0,
        maxEscalations: this.toNumber(primarySpec?.sla?.maxEscalation) ?? 0,
        onTimeout: this.normalizeTimeout(primarySpec?.sla?.onTimeout),
        escalationChain,
        worstCaseHours: this.calculateWorstCaseHours(primarySpec?.sla),
      },
      metadata: {
        id,
        createdAt: this.formatDate(workflow?.updated_date) || '',
        createdBy: 'API',
        lastEdited: this.formatDate(workflow?.updated_date) || 'just now',
        editCount: 0,
        usedBy: String(workflow?.targetEntity || workflow?.name || ''),
      },
      apiApprovalFlow: flowNode,
    };
  }

  private mapTriggerConditions(raw: any): TriggerCondition[] {
    if (Array.isArray(raw)) {
      return raw.map((item, index) => this.mapTriggerCondition(item, index));
    }

    if (!raw || typeof raw !== 'object' || !Object.keys(raw).length) {
      return [];
    }

    if ('field' in raw || 'attributeName' in raw) {
      return [this.mapTriggerCondition(raw, 0)];
    }

    return Object.entries(raw).map(([field, value], index) => this.mapTriggerCondition({ field, value }, index));
  }

  private mapTriggerCondition(raw: any, index: number): TriggerCondition {
    return {
      id: String(raw?.id || `tc-api-${index + 1}`),
      field: String(raw?.field || raw?.attributeName || ''),
      operator: String(raw?.operator || '='),
      value: raw?.value ?? raw?.attributeValue ?? '',
      currency: raw?.currency || 'USD',
    };
  }

  private mapPeople(raw: any): Array<Approver | DelegationUser> {
    if (!Array.isArray(raw)) return [];
    return raw.map((item, index) => ({
      id: Array.isArray(item?.id) ? item.id.join(',') : String(item?.id || `person-${index + 1}`),
      name: String(item?.label || item?.name || 'Approver'),
      type: String(item?.kind || item?.type || 'role').toLowerCase() === 'user' ? 'user' : 'role',
      memberCount: Array.isArray(item?.id) ? item.id.length || 1 : 1,
    }));
  }

  private mapEscalations(raw: any): EscalationStep[] {
    if (!Array.isArray(raw)) return [];
    return raw.map((item, index) => ({
      id: String(item?.id || `esc-${index + 1}`),
      name: String(item?.label || item?.name || 'Escalation manager'),
    }));
  }

  private normalizeNodeType(value: unknown): NodeType {
    const normalized = String(value || '').toUpperCase();
    return normalized === 'PARALLEL' || normalized === 'HIERARCHY' ? normalized : 'AUTO_APPROVE';
  }

  private normalizeDecisionRule(value: unknown): 'ANY_ONE' | 'N_OF_M' | 'ALL' {
    const normalized = String(value || '').toUpperCase();
    if (normalized === 'N_OF_M' || normalized === 'ALL') return normalized;
    return 'ANY_ONE';
  }

  private normalizeTimeout(value: unknown): 'ESCALATE' | 'AUTO_APPROVE' | 'REJECT' {
    const normalized = String(value || '').toUpperCase();
    if (normalized === 'AUTO_APPROVE' || normalized === 'REJECT') return normalized;
    return 'ESCALATE';
  }

  private nodeLabel(type: NodeType, index: number, spec: any): string {
    if (type === 'HIERARCHY' && spec?.level) return `Hierarchy Level ${spec.level}`;
    if (type === 'PARALLEL') return `Parallel Gate ${index + 1}`;
    if (type === 'AUTO_APPROVE') return `Auto-approve Gate ${index + 1}`;
    return `Gate ${index + 1}`;
  }

  private decisionDescription(type: NodeType, spec: any): string {
    if (type === 'HIERARCHY') return 'Sequential approval up the management chain';
    if (type === 'PARALLEL') return `${this.toNumber(spec?.minApprovals) ?? 1} approval(s) required`;
    return 'Auto-route - no human action required';
  }

  private calculateWorstCaseHours(sla: any): number | undefined {
    const reminder = this.toNumber(sla?.reminderHours);
    const maxEscalations = this.toNumber(sla?.maxEscalation);
    return reminder !== undefined && maxEscalations !== undefined ? reminder * maxEscalations : undefined;
  }

  private statusTone(status: unknown): string {
    return String(status || '').toUpperCase() === 'PUBLISHED' ? 'green' : 'neutral';
  }

  private toNumber(value: unknown): number | undefined {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private formatDate(value: unknown): string {
    if (!value) return '';
    const date = new Date(String(value));
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  private storePendingWorkflow(config: WorkflowConfig): void {
    try {
      sessionStorage.setItem(PENDING_WORKFLOW_DRAFT_KEY, JSON.stringify(config));
    } catch { /* storage unavailable */ }
  }

  private normalizeCssSize(value: string | number | undefined, fallback: string): string {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return `${value}px`;
    }
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    return fallback;
  }

  private createEmptyWorkflowForm(): { name: string; description: string; category: string } {
    return {
      name: '',
      description: '',
      category: 'General',
    };
  }
}



